import { useEffect, useState } from 'react';

// Types
import type { Environments, Paddle } from '@paddle/paddle-js';
import { initializePaddle } from '@paddle/paddle-js';
import clsx from 'clsx';

import type { CheckoutLinkDataType } from '@/utils/hooks/swr/pricing/useCheckoutLink';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
// Utils
import useUser from '@/utils/hooks/swr/useUser';

import { ActivePlanType } from '@/types/pricingTypes/subscriptionType';

type IframeCheckoutChangePaymentProps = {
    customerId: CheckoutLinkDataType['customer_id'];
    isIframeLoaded: boolean;
    onSuccess: () => void;
    setIsIframeLoaded: React.Dispatch<React.SetStateAction<boolean>>;
};

const PADDLE_CONTAINER = 'inline-checkout-container';

export function IframeCheckoutChangePayment({
    customerId,
    isIframeLoaded,
    setIsIframeLoaded,
    onSuccess
}: IframeCheckoutChangePaymentProps) {
    const { user } = useUser();
    const { subscription } = useSubscription();
    const [paddleInstance, setPaddleInstance] = useState<Paddle>(null);
    const currentSubscription = subscription as ActivePlanType;

    useEffect(() => {
        initializePaddle({
            environment: process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT as Environments,
            token: process.env.NEXT_PUBLIC_PADDLE_TOKEN,
            checkout: {
                settings: {
                    locale: user.settings.platform_language
                }
            },
            eventCallback: async (event) => {
                if (event.name === 'checkout.loaded') {
                    setIsIframeLoaded(true);
                }

                if (event.name === 'checkout.completed') {
                    onSuccess();
                }
            }
        }).then((paddle: Paddle) => {
            if (paddle) {
                setPaddleInstance(paddle);
            }
        });
    }, []);

    useEffect(() => {
        if (!isIframeLoaded && paddleInstance) {
            paddleInstance.Checkout.open({
                customer: {
                    id: customerId
                },
                transactionId: currentSubscription.transaction_id,
                settings: {
                    displayMode: 'inline',
                    frameTarget: PADDLE_CONTAINER,
                    frameInitialHeight: 380,
                    frameStyle: 'width:100%; background-color: transparent;',
                    allowDiscountRemoval: false
                }
            });
        }
    }, [isIframeLoaded, paddleInstance]);

    return <div className={clsx(PADDLE_CONTAINER, 'rounded bg-white')} />;
}
